/**
 * Waiting-room chat reaction document schemas + paths.
 *
 * Each reactor has one doc per message in the flat, week-scoped reactions
 * collection, keyed `${messageId}__${uid}` so rules can enforce self-writes
 * with a single id-pattern match and the client opens exactly one listener
 * per week regardless of message count.
 */
import {z} from 'zod';
import {FirestoreTimestampSchema} from '../types';
import type {SectionId, SectionWeekId} from '../../section/sectionApi';
import {getChatWeekDocPath} from './chatMessage';

/** Stored shape for one reactor's reaction on a message. */
export const StoredReactionDocSchema = z
  .object({
    /** The reacting user's id. */
    uid: z.string(),
    /** The reacting user's display name at reaction time (denormalized). */
    displayName: z.string(),
    /** The id of the message this reaction belongs to. Denormalized so the flat listener can group by message. */
    messageId: z.string(),
    /** Unicode emoji characters the user has reacted with. */
    emojis: z.array(z.string()),
    /** Server-assigned last-change time. */
    updatedAt: FirestoreTimestampSchema,
  })
  .loose();

/** Inferred type for {@link StoredReactionDocSchema}. */
export type StoredReactionDoc = z.infer<typeof StoredReactionDocSchema>;

/**
 * View-model for the UI: the stored doc plus its Firestore `doc.id` (which
 * always equals `${messageId}__${uid}`). The id is attached by the hook
 * after read, not part of the schema.
 */
export interface ReactionDoc extends StoredReactionDoc {
  /** Firestore document id. See `getChatReactionDocId`. */
  readonly id: string;
}

// ============================================================================
// Paths
// ============================================================================

/**
 * Flat reactions collection scoped to a week.
 *
 * @param args.sectionId - The section id.
 * @param args.sectionWeekId - The zero-indexed section week.
 * @returns Firestore collection path ending in `/reactions`.
 */
export function getChatReactionsCollectionPath(args: {
  sectionId: SectionId | string;
  sectionWeekId: SectionWeekId | number;
}): string {
  return `${getChatWeekDocPath(args)}/reactions`;
}

/**
 * Composite reaction doc id. Encodes the message id + reactor uid so (a) a
 * user can only have one reaction doc per message and (b) rules can enforce
 * self-writes by pattern-matching the id.
 *
 * @param args.messageId - The message being reacted to.
 * @param args.uid - The reactor's uid.
 * @returns The reaction doc id: `${messageId}__${uid}`.
 */
export function getChatReactionDocId(args: {
  messageId: string;
  uid: string;
}): string {
  return `${args.messageId}__${args.uid}`;
}

/**
 * Full Firestore doc path for a single reactor's reaction.
 *
 * @param args.sectionId - The section id.
 * @param args.sectionWeekId - The zero-indexed section week.
 * @param args.messageId - The message id.
 * @param args.uid - The reactor's uid.
 * @returns Firestore doc path to the reaction.
 */
export function getChatReactionDocPath(args: {
  sectionId: SectionId | string;
  sectionWeekId: SectionWeekId | number;
  messageId: string;
  uid: string;
}): string {
  const reactionDocId = getChatReactionDocId({
    messageId: args.messageId,
    uid: args.uid,
  });
  return `${getChatReactionsCollectionPath(args)}/${reactionDocId}`;
}
